import { NgIf } from '@angular/common';
import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	Output,
	ViewChild
} from '@angular/core';
import {
	ReportImage,
	ReportImageCrop
} from '../../../../services/report-images.service';
import {
	imageCrop,
	imageFrameWidth,
	isImageBeside
} from '../../../../services/report-image-layout';

/** How far one arrow key moves the point kept in view, in percent. */
const NUDGE = 2;

/**
 * One report picture, framed: the same markup and the same numbers on every
 * surface that draws a report, so the editor, the PDF and the share link show
 * the same part of the same picture at the same size.
 *
 * The card is the PDF page's shape (1440x823) wherever it is drawn, and every
 * length inside it is a share of the card's width, so a card a third the size
 * on a phone is the page scaled down rather than a different layout. Full
 * width, the picture is the card; beside its caption it is a third, a half or
 * two thirds of it, and the caption takes the rest.
 *
 * `caption` places a full-width picture's caption: over the picture, as the
 * PDF prints it; below it, as the share link lists it; or nowhere, in the
 * editor, whose card has the caption's own field under it. A picture beside
 * its caption always has it beside.
 *
 * In `cropping` mode dragging the picture, or the arrow keys, moves the point
 * kept in view; each move is emitted and nothing is kept here — the owner
 * decides what the crop is.
 */
@Component({
	standalone: true,
	selector: 'app-report-image-figure',
	templateUrl: './report-image-figure.component.html',
	styleUrls: ['./report-image-figure.component.scss'],
	imports: [NgIf]
})
export class ReportImageFigureComponent {
	@Input() image!: ReportImage;
	@Input() src = '';
	@Input() aiLabel = false;
	@Input() caption: 'overlay' | 'below' | 'none' = 'below';
	@Input() cropping = false;
	@Output() cropDrag = new EventEmitter<ReportImageCrop>();

	@ViewChild('frame') private frame?: ElementRef<HTMLElement>;
	@ViewChild('picture') private picture?: ElementRef<HTMLImageElement>;

	private drag: {
		pointerId: number;
		startX: number;
		startY: number;
		from: ReportImageCrop;
		spanX: number;
		spanY: number;
	} | null = null;

	get beside(): boolean {
		return isImageBeside(this.image);
	}

	get frameWidth(): number {
		return imageFrameWidth(this.image);
	}

	get objectPosition(): string {
		const crop = imageCrop(this.image);
		return `${crop.x}% ${crop.y}%`;
	}

	/** A scale about the point kept in view, so zooming keeps it in view. */
	get transform(): string | null {
		const zoom = imageCrop(this.image).zoom;
		return zoom > 1 ? `scale(${zoom})` : null;
	}

	onPointerDown(event: PointerEvent): void {
		const frame = this.frame?.nativeElement;
		const picture = this.picture?.nativeElement;
		if (!this.cropping || !frame || !picture?.naturalWidth) {
			return;
		}
		event.preventDefault();
		const box = frame.getBoundingClientRect();
		const from = imageCrop(this.image);
		// How far the drawn picture overhangs its frame at this zoom: moving
		// the point kept in view from 0% to 100% slides it by exactly that.
		const fit = Math.max(
			box.width / picture.naturalWidth,
			box.height / picture.naturalHeight
		);
		this.drag = {
			pointerId: event.pointerId,
			startX: event.clientX,
			startY: event.clientY,
			from,
			spanX: picture.naturalWidth * fit * from.zoom - box.width,
			spanY: picture.naturalHeight * fit * from.zoom - box.height
		};
		frame.setPointerCapture(event.pointerId);
	}

	onPointerMove(event: PointerEvent): void {
		const drag = this.drag;
		if (!drag || event.pointerId !== drag.pointerId) {
			return;
		}
		// Dragging the picture right shows more of its left, so the point
		// kept in view moves the other way.
		const along = (from: number, moved: number, span: number): number =>
			span > 0.5 ? clampPercent(from - (moved / span) * 100) : from;
		this.cropDrag.emit({
			x: along(drag.from.x, event.clientX - drag.startX, drag.spanX),
			y: along(drag.from.y, event.clientY - drag.startY, drag.spanY),
			zoom: drag.from.zoom
		});
	}

	onPointerUp(event: PointerEvent): void {
		if (this.drag?.pointerId === event.pointerId) {
			this.drag = null;
		}
	}

	onKeydown(event: KeyboardEvent): void {
		if (!this.cropping) {
			return;
		}
		const crop = imageCrop(this.image);
		const moves: Record<string, [number, number]> = {
			ArrowLeft: [NUDGE, 0],
			ArrowRight: [-NUDGE, 0],
			ArrowUp: [0, NUDGE],
			ArrowDown: [0, -NUDGE]
		};
		const move = moves[event.key];
		if (!move) {
			return;
		}
		event.preventDefault();
		this.cropDrag.emit({
			x: clampPercent(crop.x + move[0]),
			y: clampPercent(crop.y + move[1]),
			zoom: crop.zoom
		});
	}
}

/** Inside 0-100, to a tenth: what the server takes, and all a frame shows. */
function clampPercent(value: number): number {
	return Math.round(Math.min(Math.max(value, 0), 100) * 10) / 10;
}
